import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Organization } from '../../types/auth';
import { getOwnOrganization, updateOwnOrganization } from '../../services/auth';

/**
 * Organization Settings — currently just timezone, which drives the
 * "today" date-window across the UI (Brain section, Vertebra/Leg
 * pass-fail today, Dashboard "Delivered Today"). Added 2026-05-18
 * alongside migration 013_org_timezone.
 *
 * Lives under /admin/organization. Org_admin (or super_admin) only —
 * the route is gated via AdminRoute in App.tsx, and the backend
 * PATCH /api/admin/organization also enforces requireAdmin.
 *
 * The timezone dropdown is restricted to the same list the backend
 * accepts (config/timezone.SUPPORTED_TIMEZONES), but the option set
 * is delivered on GET so the two never drift.
 */

// Human-readable labels for the IANA names. Keep in sync with the
// backend's SUPPORTED_TIMEZONES, but a missing label just falls back
// to the raw zone name — no breakage if the lists drift slightly.
const TZ_LABELS: Record<string, string> = {
  'America/New_York': 'Eastern (New York) — EST/EDT',
  'America/Chicago': 'Central (Chicago) — CST/CDT',
  'America/Denver': 'Mountain (Denver) — MST/MDT',
  'America/Phoenix': 'Arizona (Phoenix) — MST, no DST',
  'America/Los_Angeles': 'Pacific (Los Angeles) — PST/PDT',
  'America/Anchorage': 'Alaska (Anchorage) — AKST/AKDT',
  'Pacific/Honolulu': 'Hawaii (Honolulu) — HST, no DST',
};

export default function OrganizationSettings() {
  const { getToken } = useAuth();

  const [org, setOrg] = useState<Organization | null>(null);
  const [supportedTimezones, setSupportedTimezones] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Form state — local until "Save", then persisted on success.
  const [draftTimezone, setDraftTimezone] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const token = await getToken();
        if (!token) { setLoadError('Your session expired. Please sign in again.'); return; }
        const data = await getOwnOrganization(token);
        setOrg(data);
        setSupportedTimezones(data.supportedTimezones || []);
        setDraftTimezone(data.timezone || 'America/New_York');
      } catch (err: any) {
        setLoadError(err?.response?.data?.error || err.message || 'Failed to load organization');
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken]);

  async function handleSave() {
    if (!draftTimezone) {return;}
    setSaving(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const token = await getToken();
      if (!token) { setSaveError('Your session expired. Please sign in again.'); return; }
      const updated = await updateOwnOrganization({ timezone: draftTimezone }, token);
      setOrg(updated);
      setSaveSuccess('Saved. "Today" counts will reflect the new timezone on the next dashboard load.');
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div><p>Loading organization settings…</p></div>;
  }
  if (loadError) {
    return <div><p className="error-message">{loadError}</p></div>;
  }
  if (!org) {
    return <div><p>No organization for current user.</p></div>;
  }

  const dirty = draftTimezone !== org.timezone;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Organization Settings</h1>
          <p className="page-subtitle">Defaults that apply to every data body under <strong>{org.name}</strong>.</p>
        </div>
      </div>

      <div className="settings-card">
        <h2>Timezone</h2>
        <p className="settings-help">
          Determines when "today" starts and ends for the metrics shown on the
          dashboard, in the Brain section, and on every vertebra and leg.
          The default is Eastern. Pick whichever zone your team uses to
          read the day's numbers.
        </p>

        {saveError && <p className="error-message">{saveError}</p>}
        {saveSuccess && <p className="success-message">{saveSuccess}</p>}

        <div className="form-group">
          <label htmlFor="org-timezone" className="form-label">Timezone</label>
          <select
            id="org-timezone"
            className="form-select"
            value={draftTimezone}
            onChange={(e) => { setDraftTimezone(e.target.value); setSaveSuccess(''); setSaveError(''); }}
            disabled={saving}
          >
            {supportedTimezones.map((tz) => (
              <option key={tz} value={tz}>{TZ_LABELS[tz] || tz}</option>
            ))}
          </select>
        </div>

        <div className="settings-actions">
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {dirty && !saving && (
            <button
              className="btn btn-secondary"
              onClick={() => { setDraftTimezone(org.timezone); setSaveError(''); setSaveSuccess(''); }}
            >
              Discard
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
